import { useEffect, useRef, useState } from "react";
import { useHoldToRecord, type HoldToRecordError } from "../../hooks/useHoldToRecord";

type Props = {
  disabled?: boolean;
  onSubmit: (blob: Blob, durationMs: number) => void;
  onError?: (reason: HoldToRecordError) => void;
  /** Optional realtime interim transcript shown live under the button while recording. */
  liveTranscript?: string;
  /** Fires when the recording lifecycle starts (true) or ends (false). Lets parents drive
   *  side effects like realtime-transcription pipelines without sniffing pointer events. */
  onRecordingChange?: (recording: boolean) => void;
};

/** Below this threshold a pointerdown→pointerup pair is treated as a TAP (latch on).
 *  At or above, it's treated as a HOLD (release stops). */
const HOLD_THRESHOLD_MS = 350;

/**
 * Voice-record button — the centerpiece of the answer surface.
 *
 * Two gestures, one button:
 *   - TAP once → starts recording and latches on. Tap again to stop.
 *   - HOLD and release → starts on press, stops on release (classic walkie-talkie).
 *
 * The distinction is the press duration. Short press (< HOLD_THRESHOLD_MS) =
 * tap-to-latch. Longer = hold-to-release. Drag-off during hold still cancels;
 * drag-off during latched-recording does NOT (user committed via tap).
 *
 * States:
 *   idle       → soft breathing ring (subtle, inviting)
 *   recording  → button warms to accent, two radiating rings pulse outward
 *   stopping   → "sending" pulse fades inward, ripple expands once
 *   submitted  → checkmark fades in for a beat, then resets
 *   error      → soft shake, "try again" label
 */
export function HoldToRecord({ disabled, onSubmit, onError, liveTranscript, onRecordingChange }: Props) {
  const { state, durationMs, start, stop, cancel } = useHoldToRecord({
    onSubmit,
    onError,
  });

  const [rippleKey, setRippleKey] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isLatched, setIsLatched] = useState(false);
  const prevState = useRef(state);
  const pressedAtRef = useRef<number | null>(null);

  // Fire a release ripple when transitioning out of recording, and emit the
  // recording-state callback so parents can drive realtime side effects.
  useEffect(() => {
    const wasRecording = prevState.current === "recording";
    const nowRecording = state === "recording";
    if (wasRecording && !nowRecording) setRippleKey((k) => k + 1);
    if (wasRecording !== nowRecording) onRecordingChange?.(nowRecording);
    if (state === "idle" || state === "error" || state === "submitted") {
      setIsLatched(false);
      pressedAtRef.current = null;
    }
    prevState.current = state;
  }, [state, onRecordingChange]);

  // Tap-once hint after 8s of idle silence.
  useEffect(() => {
    if (state !== "idle") {
      setShowHint(false);
      return;
    }
    const t = window.setTimeout(() => setShowHint(true), 8000);
    return () => window.clearTimeout(t);
  }, [state]);

  const recording = state === "recording";
  const preparing = state === "preparing";
  const busy = state === "stopping" || state === "submitted";

  const onPointerDown = () => {
    if (disabled || busy) return;
    // Tap-to-toggle: if we're already latched-recording, this tap stops it.
    if (recording && isLatched) {
      stop();
      return;
    }
    // Either fresh start, or pointerdown during a not-yet-latched in-progress hold.
    if (state === "idle" || state === "error") {
      pressedAtRef.current = Date.now();
      void start();
    }
  };

  const onPointerUp = () => {
    const pressedAt = pressedAtRef.current;
    // No press tracked (e.g., second tap-up after we already stopped on tap-down). Ignore.
    if (pressedAt === null) return;

    const elapsed = Date.now() - pressedAt;

    // Tap-and-quick-release while permission is still loading → latch when ready.
    if (preparing) {
      setIsLatched(true);
      pressedAtRef.current = null;
      return;
    }

    if (recording && !isLatched) {
      if (elapsed >= HOLD_THRESHOLD_MS) {
        // Hold release → stop normally.
        stop();
      } else {
        // Quick tap → latch on. Next tap will stop.
        setIsLatched(true);
      }
      pressedAtRef.current = null;
    }
  };

  const onPointerCancel = () => {
    // A latched recording is intentional — don't kill it just because the
    // pointer drifted off the button.
    if (isLatched) {
      pressedAtRef.current = null;
      return;
    }
    if (recording || preparing) {
      cancel();
      pressedAtRef.current = null;
    }
  };

  const label = preparing
    ? "One moment…"
    : recording
      ? isLatched
        ? "Recording"
        : formatDuration(durationMs)
      : state === "stopping"
        ? "Sending"
        : state === "submitted"
          ? "Sent"
          : state === "error"
            ? "Try once more"
            : "Tap to speak";

  const hint = recording
    ? isLatched
      ? "Tap again when you're done"
      : "Release or hold"
    : state === "idle" && showHint
      ? "Tap or press and hold"
      : null;

  return (
    <div className="flex flex-col items-center gap-5 select-none">
      <button
        type="button"
        disabled={disabled || busy}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerCancel}
        onPointerCancel={onPointerCancel}
        aria-label={label}
        aria-pressed={recording || preparing}
        className={[
          "relative w-48 h-48 rounded-full transition-all duration-500",
          "outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-4 focus-visible:ring-offset-parchment-50",
          recording
            ? "bg-accent-600 shadow-[0_0_60px_-12px_oklch(52%_0.14_30/0.45)]"
            : "bg-parchment-100 shadow-[0_2px_18px_-12px_oklch(22%_0.02_60/0.25)] hover:shadow-[0_4px_24px_-10px_oklch(22%_0.02_60/0.35)]",
          "active:scale-[0.98]",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          state === "error" ? "animate-[shake_0.32s_ease-out]" : "",
        ].join(" ")}
        style={{ touchAction: "none" }}
      >
        {/* Idle: breathing ring */}
        {state === "idle" && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full ring-[1.5px] ring-ink-300/30 breath"
          />
        )}

        {/* Recording: two radiating rings */}
        {recording && (
          <>
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full ring-2 ring-accent-600/40 radiate"
            />
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full ring-2 ring-accent-600/30 radiate"
              style={{ animationDelay: "0.5s" }}
            />
          </>
        )}

        {/* Release ripple — fires once on transition out of recording */}
        {rippleKey > 0 && (
          <span
            key={rippleKey}
            aria-hidden="true"
            className="absolute inset-0 rounded-full ring-2 ring-accent-600/60 release-ripple"
          />
        )}

        {/* Center icon — gentle morph between states */}
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex items-center justify-center transition-colors duration-500 ${
            recording ? "text-parchment-50" : "text-ink-700"
          }`}
        >
          {state === "submitted" ? <CheckIcon /> : <MicIcon recording={recording} />}
        </span>
      </button>

      <div className="flex flex-col items-center gap-1 min-h-[3rem]">
        <p
          className={`text-sm font-medium transition-colors duration-300 tabular-nums ${
            recording ? "text-accent-700" : "text-ink-700"
          }`}
        >
          {label}
        </p>
        <p
          className={`text-xs text-ink-300 transition-opacity duration-500 ${
            hint ? "opacity-100" : "opacity-0"
          }`}
        >
          {hint ?? " "}
        </p>
      </div>

      {/* Live interim transcript while recording — words appear as you speak */}
      {recording && liveTranscript && (
        <div
          className="max-w-xl text-center px-6 min-h-[2rem] transition-opacity duration-300"
          aria-live="polite"
          aria-atomic="true"
        >
          <p className="font-display italic text-base text-ink-500 leading-relaxed">
            {liveTranscript}
            <span className="ink-cursor" aria-hidden="true" />
          </p>
        </div>
      )}
    </div>
  );
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function MicIcon({ recording }: { recording: boolean }) {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={recording ? 1.6 : 1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-all duration-300"
    >
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-[ink-stroke_0.4s_cubic-bezier(0.2,0.65,0.3,1)_both]"
    >
      <polyline points="5 12 10 17 19 7" />
    </svg>
  );
}
